import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import useThemeColor from "../../hooks/useThemeColor.js";

const AddUpdateAllergenModal = ({
  show,
  onHide,
  onSubmit,
  isEdit,
  allergen,
}) => {
  const themeColor = useThemeColor();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    solution: "",
    sortOrder: 0,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit && allergen) {
      setFormData({
        name: allergen.allergen_Name || "",
        description: allergen.allergen_Description || "",
        solution: allergen.allergen_Solution || "",
        sortOrder: allergen.allergen_SortOrder || 0,
      });
    } else {
      setFormData({
        name: "",
        description: "",
        solution: "",
        sortOrder: 0,
      });
    }
    setErrors({});
  }, [isEdit, allergen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (formData.sortOrder < 0) {
      newErrors.sortOrder = "Sort order must be a positive number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const allergenId = allergen?.id ?? allergen?.ID ?? 0;
    const submitData = {
      id: allergenId,
      name: formData.name.trim(),
      description: formData.description.trim(),
      solution: formData.solution.trim(),
      sortOrder: parseInt(formData.sortOrder, 10) || 0,
    };
    onSubmit(submitData, isEdit, false);
  };

  const handleClose = () => {
    setFormData({
      name: "",
      description: "",
      solution: "",
      sortOrder: 0,
    });
    setErrors({});
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {isEdit ? "Edit Allergen" : "Add New Allergen"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g. Gluten"
                  isInvalid={!!errors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter allergen description"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Solution</Form.Label>
                <Form.Control
                  type="text"
                  name="solution"
                  value={formData.solution}
                  onChange={handleInputChange}
                  placeholder="e.g. Gluten-free"
                  maxLength={250}
                />
                <Form.Text muted>
                  Generic option offered to the customer (e.g. Gluten-free).
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Sort Order</Form.Label>
                <Form.Control
                  type="number"
                  name="sortOrder"
                  value={formData.sortOrder}
                  onChange={handleInputChange}
                  placeholder="Enter sort order"
                  min="0"
                  isInvalid={!!errors.sortOrder}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.sortOrder}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          style={{
            backgroundColor: themeColor,
            borderColor: themeColor,
          }}
        >
          {isEdit ? "Update Allergen" : "Add Allergen"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddUpdateAllergenModal;
